// src/pages/ArticlePage.jsx
import React, { useEffect, useRef, useContext } from "react";
import { Link, useParams } from "react-router-dom";
import { useAnalytics } from "../hooks/useAnalytics";
import "./ArticlePage.css";

// compliance
import ComplianceBanner from "../components/ComplianceBanner";

import { LangContext } from "../lang/LangProvider";
import Seo from "../components/Seo";
import { SITE_URL } from "../config/site";

const ArticlePage = () => {
  const { lang, t, articles } = useContext(LangContext);
  const { slug } = useParams();
  const article = articles.find((a) => a.slug === slug);
  const { trackArticleOpen, trackArticleRead } = useAnalytics();
  const readTracked = useRef(false);
  const openTime = useRef(Date.now());

  // Traccia apertura articolo
  useEffect(() => {
    if (!article) return;
    trackArticleOpen(slug, lang);
    readTracked.current = false;
    openTime.current = Date.now();
  }, [slug, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  // Traccia lettura completa (scroll > 85%)
  useEffect(() => {
    if (!article) return;
    const handleScroll = () => {
      if (readTracked.current) return;
      const scrolled = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      if (scrolled / total > 0.85) {
        const seconds = Math.round((Date.now() - openTime.current) / 1000);
        trackArticleRead(slug, lang, seconds);
        readTracked.current = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [slug, lang, article]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!article) {
    return (
      <div className="ap-wrap">
        <div className="ap-container">
          <p>{lang === "en" ? "Article not found." : "Articolo non trovato."}</p>
          <p>
            <Link to={`/${lang}/kb`} className="ap-back">
              {t("article.back")}
            </Link>
          </p>
        </div>
      </div>
    );
  }

  const fmt = lang === "en" ? "en-US" : "it-IT";

  // Immagine assoluta per og:image / JSON-LD (la coverImage è un asset webpack "/static/...")
  const coverAbs = article.coverImage
    ? String(article.coverImage).startsWith("http")
      ? article.coverImage
      : `${SITE_URL}${article.coverImage}`
    : undefined;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.metaDescription,
    datePublished: article.datePublished,
    dateModified: article.dateUpdated || article.datePublished,
    author: [{ "@type": "Person", name: article.author || "Redazione" }],
    mainEntityOfPage: `${SITE_URL}/${lang}/kb/${slug}`,
    image: coverAbs ? [coverAbs] : undefined,
    inLanguage: lang,
    publisher: {
      "@type": "Organization",
      name: "AlphaFrame Analytics",
      logo: { "@type": "ImageObject", url: `${SITE_URL}/logo-512.png` },
    },
  };

  return (
    <div className="ap-wrap">
      <Seo
        title={article.title}
        description={article.metaDescription}
        path={`/${lang}/kb/${slug}`}
        type="article"
        image={article.coverImage}
        hreflang={false}
        jsonLd={articleJsonLd}
      />
      <nav className="ap-breadcrumb" aria-label="breadcrumb">
        <ol>
          <li>
            <Link to={`/${lang}/`}>{lang === "en" ? "Home" : "Home"}</Link>
          </li>
          <li>
            <Link to={`/${lang}/kb`}>{t("kb.title")}</Link>
          </li>
          <li aria-current="page">{article.title}</li>
        </ol>
      </nav>

      {/* Banner compliance (se serve mantenerlo visibile) */}
      <ComplianceBanner />

      <article className="ap-container" itemScope itemType="https://schema.org/Article">
        {article.coverImage && (
          <figure className="ap-cover">
            <img src={article.coverImage} alt="" loading="lazy" />
          </figure>
        )}

        <header className="ap-header">
          <h1 itemProp="headline">{article.title}</h1>
          <p className="ap-meta">
            <span>✍️ {article.author || (lang === "en" ? "Editorial" : "Redazione")}</span>
            <span>•</span>
            {article.datePublished ? (
              <time dateTime={article.datePublished}>
                {lang === "en" ? "Published" : "Pubblicato"}{" "}
                {new Date(article.datePublished).toLocaleDateString(fmt)}
              </time>
            ) : null}
            {article.dateUpdated && (
              <>
                <span>•</span>
                <time dateTime={article.dateUpdated}>
                  {lang === "en" ? "Updated" : "Aggiornato"}{" "}
                  {new Date(article.dateUpdated).toLocaleDateString(fmt)}
                </time>
              </>
            )}
            {article.readTime && (
              <>
                <span>•</span>
                <span>
                  {article.readTime} {t("kb.readTime")}
                </span>
              </>
            )}
          </p>
        </header>

        <section className="ap-content" itemProp="articleBody">
          {article.content}
        </section>

        <footer className="ap-footer">
          {article.tags?.length ? (
            <div className="ap-tags">
              {article.tags.map((tg) => (
                <Link key={tg} to={`/${lang}/kb?tag=${encodeURIComponent(tg)}`} className="ap-tag">
                  #{tg}
                </Link>
              ))}
            </div>
          ) : null}

          {/* Revisione 29/09/2026: niente CTA commerciali né waitlist (servizio non avviato);
              il vecchio link puntava a /contact, rotta inesistente. */}
          <div className="ap-cta">
            <Link to={`/${lang}/about`} className="ap-btn">
              {lang === "en" ? "General contact" : "Contatti generali"}
            </Link>
            <Link to={`/${lang}/kb`} className="ap-back">
              {t("article.back")}
            </Link>
          </div>
        </footer>

        <meta itemProp="datePublished" content={article.datePublished || ""} />
        <meta itemProp="dateModified" content={article.dateUpdated || article.datePublished || ""} />
      </article>
    </div>
  );
};

export default ArticlePage;
